/**
 * ConnectionPill — WS connection state (TASK-711).
 * Green connected / amber reconnecting / red offline; optional reconnect action.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Wifi, WifiOff, RotateCw } from 'lucide-react';

export interface ConnectionPillProps {
  isConnected: boolean;
  isReconnecting: boolean;
  /** Shown only when offline (not while a retry is in flight). */
  onReconnect?: () => void;
}

export const ConnectionPill: React.FC<ConnectionPillProps> = ({
  isConnected,
  isReconnecting,
  onReconnect,
}) => {
  const { t } = useTranslation('common');
  if (isConnected) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-emerald-600"
        data-testid="dock-connection"
        data-state="connected"
        title={t('dock.connected', 'Connected')}
      >
        <Wifi className="h-3.5 w-3.5" />
      </span>
    );
  }

  if (isReconnecting) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-amber-600"
        data-testid="dock-connection"
        data-state="reconnecting"
      >
        <RotateCw className="h-3.5 w-3.5 animate-spin" />
        {t('dock.reconnecting', 'Reconnecting…')}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-red-500"
      data-testid="dock-connection"
      data-state="offline"
    >
      <WifiOff className="h-3.5 w-3.5" />
      {t('dock.offline', 'Offline')}
      {onReconnect && (
        <button
          type="button"
          onClick={onReconnect}
          className="ml-1 underline decoration-dotted hover:text-red-700"
        >
          {t('dock.reconnect', 'Reconnect')}
        </button>
      )}
    </span>
  );
};
